(() => {
  const SELECTOR = '[data-brand-cart-promotions]';
  const POETIC_GIFT_OFFER = 'poetic_dance_10ml_spend_200_free';
  let syncInFlight = false;

  const moneyFromCents = (cents) =>
    new Intl.NumberFormat(window.Shopify?.country === 'AU' ? 'en-AU' : undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'AUD',
      maximumFractionDigits: 0,
    }).format(Math.max(0, cents) / 100);

  const getSettings = () => {
    const root = document.querySelector(SELECTOR);
    if (!root) return null;

    return {
      root,
      freeShippingThreshold: Number(root.dataset.freeShippingThreshold || 0),
      giftEnabled: root.dataset.gwpEnabled === 'true',
      giftThreshold: Number(root.dataset.gwpThreshold || 0),
      giftVariantId: Number(root.dataset.gwpVariantId || 0),
      poeticGiftThreshold: Number(root.dataset.poeticGiftThreshold || 0),
      poeticGiftVariantId: Number(root.dataset.poeticGiftVariantId || 0),
    };
  };

  const getQualifyingTotal = (cart, giftVariantId) =>
    cart.items.reduce((total, item) => {
      if (item.variant_id === giftVariantId && item.properties?._BrandGwp === 'threshold') return total;
      return total + item.final_line_price;
    }, 0);

  const getPoeticGiftQualifyingTotal = (cart) =>
    cart.items.reduce((total, item) => {
      if (item.properties?._discount_kit_offer === POETIC_GIFT_OFFER) return total;
      return total + item.final_line_price;
    }, 0);

  const updateProgressCopy = (cart, settings) => {
    document.querySelectorAll('[data-brand-progress]').forEach((element) => {
      const threshold = Number(element.dataset.threshold || 0);
      if (!threshold) return;

      const qualifyingTotal =
        element.dataset.brandProgress === 'gift'
          ? getQualifyingTotal(cart, settings.giftVariantId)
          : element.dataset.brandProgress === 'poetic-gift'
            ? getPoeticGiftQualifyingTotal(cart)
            : cart.total_price;
      const remaining = Math.max(threshold - qualifyingTotal, 0);
      const percent = Math.min((qualifyingTotal / threshold) * 100, 100);

      element.style.setProperty('--progress', `${percent}%`);

      const amount = element.querySelector('[data-brand-progress-remaining]');
      if (amount) amount.textContent = moneyFromCents(remaining);

      element.toggleAttribute('data-complete', remaining === 0);
    });
  };

  const getSectionsToRefresh = () => {
    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    if (mainCartItems?.dataset.id) sections.push(mainCartItems.dataset.id);
    if (mainCartFooter?.dataset.id) sections.push(mainCartFooter.dataset.id);

    return [...new Set(sections)];
  };

  const refreshCartPageSections = (state) => {
    if (!state?.sections) return;

    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    if (mainCartItems?.dataset.id && state.sections[mainCartItems.dataset.id]) {
      const html = new DOMParser().parseFromString(state.sections[mainCartItems.dataset.id], 'text/html');
      const source = html.querySelector('cart-items');
      const target = document.querySelector('cart-items');
      if (source && target) target.replaceWith(source);
    }

    if (mainCartFooter?.dataset.id && state.sections[mainCartFooter.dataset.id]) {
      const html = new DOMParser().parseFromString(state.sections[mainCartFooter.dataset.id], 'text/html');
      const source = html.querySelector('#main-cart-footer');
      if (source) mainCartFooter.replaceWith(source);
    }
  };

  const fetchCartMutation = async (url, body) => {
    const response = await fetch(url, {
      ...fetchConfig('javascript'),
      body: JSON.stringify(body),
    });
    const state = await response.json();
    refreshCartPageSections(state);
    return state;
  };

  const syncGift = async (cart, settings) => {
    if (!settings.giftEnabled || !settings.giftVariantId || !settings.giftThreshold || syncInFlight) return;

    const qualifyingTotal = getQualifyingTotal(cart, settings.giftVariantId);
    const giftItems = cart.items.filter(
      (item) => item.variant_id === settings.giftVariantId && item.properties?._BrandGwp === 'threshold'
    );
    const shouldHaveGift = qualifyingTotal >= settings.giftThreshold;

    if (shouldHaveGift && giftItems.length === 0) {
      syncInFlight = true;
      await fetchCartMutation(`${routes.cart_add_url}`, {
        items: [
          {
            id: settings.giftVariantId,
            quantity: 1,
            properties: {
              _BrandGwp: 'threshold',
            },
          },
        ],
        sections: getSectionsToRefresh(),
        sections_url: window.location.pathname,
      });
      syncInFlight = false;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-promotions' });
      return;
    }

    if (!shouldHaveGift && giftItems.length > 0) {
      syncInFlight = true;
      const updates = giftItems.reduce((payload, item) => {
        payload[item.key] = 0;
        return payload;
      }, {});

      await fetchCartMutation(`${routes.cart_update_url}`, {
        updates,
        sections: getSectionsToRefresh(),
        sections_url: window.location.pathname,
      });
      syncInFlight = false;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-promotions' });
    }
  };

  const syncPoeticDanceGift = async (cart, settings) => {
    if (!settings.poeticGiftThreshold || !settings.poeticGiftVariantId || syncInFlight) return;

    const qualifyingTotal = getPoeticGiftQualifyingTotal(cart);
    const giftItems = cart.items.filter(
      (item) =>
        item.variant_id === settings.poeticGiftVariantId && item.properties?._discount_kit_offer === POETIC_GIFT_OFFER
    );
    const shouldHaveGift = qualifyingTotal >= settings.poeticGiftThreshold;

    if (shouldHaveGift && giftItems.length === 0) {
      syncInFlight = true;
      await fetchCartMutation(`${routes.cart_add_url}`, {
        items: [
          {
            id: settings.poeticGiftVariantId,
            quantity: 1,
            properties: {
              _discount_kit_offer: POETIC_GIFT_OFFER,
            },
          },
        ],
        sections: getSectionsToRefresh(),
        sections_url: window.location.pathname,
      });
      syncInFlight = false;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-promotions' });
      return;
    }

    if (!shouldHaveGift && giftItems.length > 0) {
      syncInFlight = true;
      const updates = giftItems.reduce((payload, item) => {
        payload[item.key] = 0;
        return payload;
      }, {});

      await fetchCartMutation(`${routes.cart_update_url}`, {
        updates,
        sections: getSectionsToRefresh(),
        sections_url: window.location.pathname,
      });
      syncInFlight = false;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-promotions' });
    }
  };

  const syncCartPromotions = async () => {
    const settings = getSettings();
    if (!settings) return;

    try {
      const cart = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' }).then((response) => response.json());
      updateProgressCopy(cart, settings);
      await syncGift(cart, settings);
      await syncPoeticDanceGift(cart, settings);
    } catch (error) {
      console.error('Brand cart promotions failed to sync', error);
    } finally {
      syncInFlight = false;
    }
  };

  document.addEventListener('DOMContentLoaded', syncCartPromotions);
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === 'brand-cart-promotions') return;
    window.setTimeout(syncCartPromotions, 120);
  });
})();
